/**
 * The send ledger's vocabulary (FEAT-038 wave 2).
 *
 * One row per (media × destination). Two moments to a friend and a group is
 * four rows, one upload each — the media is uploaded once and every row for it
 * claims or copies the same staged upload.
 *
 * The row is LABELS ONLY (`bytes?: never`). The media lives in `mediaStore`
 * under `mediaId` and is read one 2 MiB chunk at a time, at the one moment its
 * upload is actually moving. That separation is the whole point of the wave:
 * no read of this store, for any reason, can materialise a video.
 */
import type { MomentOverlays, StoryAudience } from '../../types';

/** Where a moment is going. The destination is committed to the row at
 * send-tap, before a single byte moves — BUG-119 was a stuck send that had
 * forgotten its recipient, because the destination was only written down
 * AFTER a direct POST had already failed. */
export type Destination =
  | { kind: 'friend'; friendId: number }
  | { kind: 'group'; groupId: number }
  | {
      kind: 'story';
      audience: StoryAudience;
      viewerIds?: number[];
      batchId?: string;
      batchPosition?: number;
    }
  | { kind: 'vault'; isPrivate?: boolean; albumId?: number };

/**
 * Where a row is in its life. Every transition is written to disk BEFORE it is
 * acted on, so a kill can only ever lose work that had not started.
 *
 * queued    — committed, nothing on the wire (the normal offline state)
 * uploading — chunks are going up; the server's offset is the truth
 * uploaded  — every byte is on the server, NOT yet finalized. Finalizing
 *             starts the staged row's one-hour reaper clock, so it waits until
 *             a destination POST is actually about to follow.
 * sending   — finalized (or staged), the destination POST is in flight
 * done      — the server has it; the row is about to be deleted
 * stuck     — needs a human: a refusal, five failures, or unreadable media
 */
export type RowState =
  | 'queued'
  | 'uploading'
  | 'uploaded'
  | 'sending'
  | 'done'
  | 'stuck';

export interface QueueRow {
  /** The row id IS the client key: `<mediaId>:f12` / `:g3` / `:s` / `:v`.
   * Same shape the fan-out has always stamped on drafts, so a key stamped by
   * an older build still converges on the server. */
  id: string;
  mediaId: string;
  clientKey: string;
  dest: Destination;
  state: RowState;
  /** Media size in bytes, copied from the meta row at enqueue — which is what
   * lets small-first ordering and lane choice stay metadata questions. */
  size: number;
  mediaType: 'photo' | 'video';
  caption?: string;
  overlays?: MomentOverlays | null;
  retentionType?: string;
  replayAllowed?: boolean;
  hd?: boolean;
  facing?: 'user' | 'environment';
  /** A Memory being re-shared: the server copies it and NO bytes move. */
  savedItemId?: number;
  /** The draft this row delivers (normally the mediaId itself). When the last
   * row of a draft lands, the draft is done. */
  draftId?: string;
  /** The optimistic bubble waiting on this row (messageStore/groupStore). */
  optimisticId?: string;
  /**
   * A row adopted from the retired outbox whose media is still INSIDE its old
   * record (the evacuation parked it). Its bytes are read from there, once, at
   * its upload turn — never at boot.
   */
  legacyRowId?: string;
  createdAt: number;
  updatedAt: number;
  attempts: number;
  lastError?: string;
  /** Why a human is needed. Only set with `state: 'stuck'`. */
  stuckReason?: string;
  /** Earliest wall-clock for the next attempt (the backoff ladder). */
  nextAttemptAt?: number;
  /** The server said 401 while this row was moving. Persisted so a relaunch
   * can say "waiting for sign-in" without asking the network first. */
  pausedAuth?: boolean;
  /** The disease, banned at the type level. */
  bytes?: never;
}

/** One upload's server-side state, keyed by media — NOT by row, because every
 * destination of one moment shares one upload. */
export interface SessionRow {
  mediaId: string;
  /** The chunked session, once it exists. Null while a heavy row is queued
   * offline: sessions are created lazily at the first upload turn. */
  uploadId: string | null;
  /** Bytes the server has committed. The client never guesses this. */
  committedOffset: number;
  /** Bytes fed to the streaming hash, and the hasher's state at that point.
   * A checkpoint is what stops a relaunch re-reading a 100 MB file to learn a
   * checksum it had already computed. */
  hashedBytes: number;
  hashState?: Uint8Array;
  /** The finalized (or directly staged) upload the destinations will claim. */
  stagedId?: string;
  /** When `stagedId` was minted — the server reaps unclaimed staged rows after
   * an hour, so past 50 minutes we upload again rather than 404 in a loop. */
  stagedAt?: number;
  totalBytes: number;
  updatedAt: number;
}

/** What the pill and the update guard read. Shape preserved from the outbox
 * it replaces: sends and saves are counted apart because "Saving 2 items" and
 * "Sending 1 moment" are different sentences (BUG-110). */
export interface SendState {
  pending: number;
  stuck: number;
  offline: boolean;
  /** Something is waiting on a sign-in (BUG-069) — safe, not failed. */
  needsAuth: boolean;
  saving: number;
  savingStuck: number;
}

export type SendServerResult = unknown;

export type SendResult =
  | { success: true; row: QueueRow; server: SendServerResult }
  | { success: false; row: QueueRow; error: string; permanent: boolean };

/** One moment, on its way to one or more places. */
export interface SendIntent {
  mediaId: string;
  destinations: Destination[];
  mediaType: 'photo' | 'video';
  /** Media size — the caller knows it; the ledger must not read bytes to find
   * out. Omitted only when the media lives in the store already (looked up
   * from its meta row, still metadata). */
  size?: number;
  caption?: string;
  overlays?: MomentOverlays | null;
  retentionType?: string;
  replayAllowed?: boolean;
  hd?: boolean;
  facing?: 'user' | 'environment';
  savedItemId?: number;
  draftId?: string;
  /** Per-destination optimistic bubble ids, keyed by client key. */
  optimisticIds?: Record<string, string>;
}

/** The client key a (media, destination) pair always goes out under. Stable by
 * construction, which is what makes every retry converge server-side. */
export function clientKeyFor(mediaId: string, dest: Destination): string {
  switch (dest.kind) {
    case 'friend':
      return `${mediaId}:f${dest.friendId}`;
    case 'group':
      return `${mediaId}:g${dest.groupId}`;
    case 'story':
      return `${mediaId}:s`;
    case 'vault':
      // The private/public split has always been part of the key: saving the
      // same moment to Memories and to Only Me is two saves, not a duplicate.
      return `${mediaId}:v${dest.isPrivate ? 'p' : ''}`;
  }
}
